import { memo, useCallback } from 'react';

import {
  BulkEditDateValueCustomFieldModal,
  type BulkEditDateValueCustomFieldModalProps,
} from '~/components/Modals/BulkEditCustomFieldModal/BulkEditDateValueCustomFieldModal';
import { useSelectAllCustomFieldModal } from '~/hooks/customFields/useSelectAllCustomFieldModal';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useUpdateItemsCustomFieldValue } from '~/utils/CustomFields/useUpdateItemsCustomFieldValue';
import { type BulkEditCustomFieldModalProps } from './types';

export type PrivateBulkEditDateValueCustomFieldModalProps = Omit<BulkEditCustomFieldModalProps, 'onSave'>;

const resolveInitialValue: Parameters<typeof useSelectAllCustomFieldModal>[0]['resolveInitialValue'] = (
  common,
  field,
) => {
  const dateValue = common.value && 'dateValue' in common.value ? common.value.dateValue : null;

  return {
    ...field,
    value: null,
    values: null,
    plainTextValue: undefined,
    dateValue: dateValue ?? undefined,
  };
};

export const PrivateBulkEditDateValueCustomFieldModal = memo(
  (props: AirModalProps<PrivateBulkEditDateValueCustomFieldModalProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceId = currentWorkspace?.id;
    const { updateItemsCustomFieldValue } = useUpdateItemsCustomFieldValue();

    const { selectAll, initialValues, initialStatus, isLoadingInitialValues, handleSelectAllSave } =
      useSelectAllCustomFieldModal({
        field: props.field,
        fallbackInitialValues: props.initialValues,
        resolveInitialValue,
      });

    const onSave = useCallback<BulkEditDateValueCustomFieldModalProps['onSave']>(
      async (params) => {
        if (selectAll) {
          await handleSelectAllSave({
            baseCustomField: params.baseCustomField,
            operation: 'date',
            trackLocation: 'context-menu-bulk-edit',
            dateValue: params.nextDateValue,
          });
          return;
        }

        await updateItemsCustomFieldValue({
          ...params,
          trackingLocation: 'context-menu-bulk-edit',
          workspaceId,
        });
      },
      [handleSelectAllSave, selectAll, updateItemsCustomFieldValue, workspaceId],
    );

    return (
      <BulkEditDateValueCustomFieldModal
        {...props}
        initialValues={initialValues}
        initialStatus={initialStatus}
        isLoadingInitialValues={isLoadingInitialValues}
        onSave={onSave}
      />
    );
  },
);
PrivateBulkEditDateValueCustomFieldModal.displayName = 'PrivateBulkEditDateValueCustomFieldModal';
